import React, { useContext, useEffect } from 'react';
import FormField from '../../../form/FormField';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import AlertStatusSetupContext, { MonitoringSetupDataType } from './AlertStatusSetupContext';
import { SalvFieldHeader } from '@salvapp/ui-components';

const validationSchema: Yup.Schema<MonitoringSetupDataType> = Yup.object()
  .shape({
    alertSubtype: Yup.object().shape({
      isOnline: Yup.boolean().required(),
      isOffline: Yup.boolean().required(),
      isPeriodic: Yup.boolean().required(),
    }),
  })
  .test({
    test: (values) => {
      if (values.alertSubtype.isOnline || values.alertSubtype.isOffline || values.alertSubtype.isPeriodic) {
        return true;
      } else {
        return new Yup.ValidationError('At least one alert type has to be chosen', null, 'alertSubtype');
      }
    },
  });

const MonitoringSetup = () => {
  const { formRef, monitoringSetup, progressStepper } = useContext(AlertStatusSetupContext)!;

  const { data, setData, setValid, isSubmissionTouched, setSubmitted } = monitoringSetup;
  const { setMonitoringSetupStep } = progressStepper;

  const submit = (values: MonitoringSetupDataType) => {
    setData(values);
    setSubmitted(true);
    setMonitoringSetupStep({ type: 'filled' });
  };

  return (
    <div>
      <h2 className={'title-m m-b-12'}>Monitoring setup</h2>
      <Formik initialValues={data} validationSchema={validationSchema} onSubmit={submit} innerRef={formRef}>
        {({ setFieldValue, values, errors, isValid }: FormikProps<MonitoringSetupDataType>) => {
          useEffect(() => {
            setValid(isValid);
          }, [isValid]);

          return (
            <>
              <div className={'m-b-24'}>
                <SalvFieldHeader
                  label={'Alert type'}
                  description={'Choose alerts based on the alert type for which you want to enable this status.'}
                />
                <div className={`list-box ${isSubmissionTouched && errors.alertSubtype ? 'invalid' : ''}`}>
                  <div>Real-time</div>
                  <FormField
                    id={'isOnline'}
                    name={'isOnline'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertSubtype.isOnline', e.target.checked);
                    }}
                    checked={values.alertSubtype.isOnline}
                    isInvalid={isSubmissionTouched && !!errors.alertSubtype}
                  />
                </div>
                <div className={`list-box ${isSubmissionTouched && errors.alertSubtype ? 'invalid' : ''}`}>
                  <div>Post-event</div>
                  <FormField
                    id={'isOffline'}
                    name={'isOffline'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertSubtype.isOffline', e.target.checked);
                    }}
                    checked={values.alertSubtype.isOffline}
                    isInvalid={isSubmissionTouched && !!errors.alertSubtype}
                  />
                </div>
                <div className={`list-box ${isSubmissionTouched && errors.alertSubtype ? 'invalid' : ''}`}>
                  <div>Periodic</div>
                  <FormField
                    id={'isPeriodic'}
                    name={'isPeriodic'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertSubtype.isPeriodic', e.target.checked);
                    }}
                    checked={values.alertSubtype.isPeriodic}
                    isInvalid={isSubmissionTouched && !!errors.alertSubtype}
                  />
                </div>
              </div>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default MonitoringSetup;
